import React, { useState, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Divider,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Download as DownloadIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { CSVItem, CSVValidationError, parseCSV, downloadCSVTemplate, normalizeItemType } from '../../utils/csvUtils';

interface CSVUploadDialogProps {
  open: boolean;
  onClose: () => void;
  onImport: (items: CSVItem[], replaceExisting: boolean) => void;
  existingItemCount: number;
}

const CSVUploadDialog: React.FC<CSVUploadDialogProps> = ({
  open,
  onClose,
  onImport,
  existingItemCount
}) => {
  const [csvData, setCsvData] = useState<string>('');
  const [validationResult, setValidationResult] = useState<any>(null);
  const [replaceExisting, setReplaceExisting] = useState<boolean>(false);
  const [isPreviewMode, setIsPreviewMode] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Check file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      alert('File size must be less than 5MB');
      return;
    }

    // Check file type
    if (!file.name.toLowerCase().endsWith('.csv')) {
      alert('Please select a CSV file');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      setCsvData(text);
      const result = parseCSV(text);
      setValidationResult(result);
      setIsPreviewMode(true);
    };
    reader.readAsText(file);
  };

  const handleDownloadTemplate = () => {
    downloadCSVTemplate();
  };

  const handleImport = () => {
    if (validationResult && validationResult.isValid) {
      onImport(validationResult.items, replaceExisting);
      handleClose();
    }
  };

  const handleClose = () => {
    setCsvData('');
    setValidationResult(null);
    setIsPreviewMode(false);
    setReplaceExisting(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onClose();
  };

  const getItemTypeLabel = (type: string) => {
    const normalized = normalizeItemType(type);
    const labels: { [key: string]: string } = {
      'text_input': 'Text Input',
      'number_input': 'Number Input',
      'checkbox': 'Checkbox',
      'file_upload': 'File Upload',
      'date_input': 'Date Input',
      'select': 'Dropdown',
      'multi_select': 'Multi-select'
    };
    return labels[normalized] || type;
  };

  const renderPreview = () => {
    if (!validationResult) return null;

    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          CSV Preview
        </Typography>
        
        {validationResult.errors.length > 0 && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Found {validationResult.errors.length} validation error(s):
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2 }}>
              {validationResult.errors.map((error: CSVValidationError, index: number) => (
                <li key={index}>
                  <Typography variant="body2">
                    Row {error.row}, Column "{error.column}": {error.message}
                  </Typography>
                </li>
              ))}
            </Box>
          </Alert>
        )}

        {validationResult.items.length > 0 && (
          <Box>
            <Typography variant="subtitle1" gutterBottom>
              Valid Items ({validationResult.items.length}):
            </Typography>
            <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Order</TableCell>
                    <TableCell>Title</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Required</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {validationResult.items.map((item: CSVItem, index: number) => (
                    <TableRow key={index}>
                      <TableCell>{item.order}</TableCell>
                      <TableCell>{item.title}</TableCell>
                      <TableCell>{item.description}</TableCell>
                      <TableCell>
                        <Chip 
                          label={getItemTypeLabel(item.item_type)} 
                          size="small" 
                          variant="outlined" 
                        />
                      </TableCell>
                      <TableCell>
                        {item.is_required ? (
                          <Chip label="Required" size="small" color="error" />
                        ) : (
                          <Chip label="Optional" size="small" variant="outlined" />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {validationResult.isValid && (
          <Box mt={2}>
            <FormControl component="fieldset">
              <Typography variant="subtitle1" gutterBottom>
                Import Options:
              </Typography>
              <RadioGroup
                value={replaceExisting ? 'replace' : 'append'}
                onChange={(e) => setReplaceExisting(e.target.value === 'replace')}
              >
                <FormControlLabel
                  value="append"
                  control={<Radio />}
                  label={`Add to existing items (${existingItemCount} current items)`}
                />
                <FormControlLabel
                  value="replace"
                  control={<Radio />}
                  label="Replace all existing items"
                />
              </RadioGroup>
            </FormControl>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Import Checklist Items from CSV</Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {!isPreviewMode ? (
          <Box>
            <Typography variant="body1" gutterBottom>
              Upload a CSV file to import checklist items. The CSV should have the following columns:
            </Typography>
            
            <Box component="ul" sx={{ mb: 3 }}>
              <li><strong>title</strong> - The item title (required)</li>
              <li><strong>description</strong> - Item description (optional)</li>
              <li><strong>item_type</strong> - Type of item (Text, Number, Checkbox, File Upload, Date, Dropdown, Multi-select)</li>
              <li><strong>order</strong> - Display order (number)</li>
              <li><strong>is_required</strong> - Whether item is required (true/false)</li>
              <li><strong>options</strong> - For Dropdown, Multi-select, or Radio items: semicolon-separated choices (e.g. <code>Yes;No;N/A</code>)</li>
            </Box>

            <Box display="flex" gap={2} mb={3}>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleDownloadTemplate}
              >
                Download Template
              </Button>
            </Box>

            <Box
              border="2px dashed"
              borderColor="primary.main"
              borderRadius={2}
              p={3}
              textAlign="center"
              sx={{ cursor: 'pointer' }}
              onClick={() => fileInputRef.current?.click()}
            >
              <UploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Click to select CSV file
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Maximum file size: 5MB
              </Typography>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
            </Box>
          </Box>
        ) : (
          renderPreview()
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>
          Cancel
        </Button>
        {isPreviewMode && validationResult?.isValid && (
          <Button
            variant="contained"
            onClick={handleImport}
            startIcon={<CheckIcon />}
          >
            Import {validationResult.items.length} Items
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CSVUploadDialog; 